import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useI18n } from "@/i18n";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export default function Header() {
  const { t, lang } = useI18n();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const base = `/${lang}`;
  const home = base;

  // Home page needs anchor links; other pages need to go back home first
  const isHome = location.pathname === base || location.pathname === `${base}/`;
  const link = (hash) => (isHome ? `#${hash}` : `${base}#${hash}`);

  const navItems = [
    { key: "nav.home", href: home, isHomeLink: true },
    { key: "nav.about", href: link("plataforma") },
    { key: "nav.sectors", href: link("sectores") },
    { key: "nav.process", href: link("proceso") },
  ];

  return (
    <header className="header-solid text-white sticky top-0 z-40 border-b border-white/10" data-testid="site-header">
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10 py-4 flex items-center justify-between gap-6">
        <Link to={home} className="flex items-center gap-3" data-testid="header-logo-link">
          <span className="inline-flex items-center justify-center w-9 h-9 border border-white/40" aria-hidden="true">
            <span className="font-serif-brand font-bold tracking-tight text-white text-lg">X</span>
          </span>
          <span className="flex flex-col leading-tight">
            <span className="font-serif-brand text-xl font-semibold tracking-tight">Xorzeni</span>
            <span className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-white/60">{t("common.brandTagline")}</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8" aria-label="primary">
          {navItems.map((item, i) =>
            item.isHomeLink ? (
              <Link
                key={i}
                to={item.href}
                className="text-sm text-white/80 hover:text-white font-medium tracking-wide"
                data-testid={`nav-desktop-${item.key}`}
              >
                {t(item.key)}
              </Link>
            ) : (
              <a
                key={i}
                href={item.href}
                className="text-sm text-white/80 hover:text-white font-medium tracking-wide"
                data-testid={`nav-desktop-${item.key}`}
              >
                {t(item.key)}
              </a>
            ),
          )}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <LanguageSwitcher />
          <a
            href={link("newsletter")}
            className="inline-flex items-center bg-[var(--brand-terracotta)] hover:bg-[var(--brand-terracotta-2)] px-4 py-2 text-sm font-medium text-white"
            data-testid="header-cta-newsletter"
          >
            {t("hero.ctaPrimary")}
          </a>
        </div>

        <button
          type="button"
          className="lg:hidden inline-flex items-center justify-center w-10 h-10 border border-white/30"
          aria-label={open ? t("nav.menuClose") : t("nav.menuOpen")}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-white/10 header-solid" data-testid="mobile-menu">
          <div className="max-w-[1240px] mx-auto px-6 py-6 flex flex-col gap-5">
            {navItems.map((item, i) =>
              item.isHomeLink ? (
                <Link
                  key={i}
                  to={item.href}
                  className="text-base text-white/90 font-medium"
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-${item.key}`}
                >
                  {t(item.key)}
                </Link>
              ) : (
                <a
                  key={i}
                  href={item.href}
                  className="text-base text-white/90 font-medium"
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-${item.key}`}
                >
                  {t(item.key)}
                </a>
              ),
            )}
            <div className="pt-4 border-t border-white/10 flex items-center justify-between">
              <LanguageSwitcher onSwitch={() => setOpen(false)} />
              <a
                href={link("newsletter")}
                onClick={() => setOpen(false)}
                className="bg-[var(--brand-terracotta)] px-4 py-2 text-sm font-medium text-white"
                data-testid="mobile-cta-newsletter"
              >
                {t("hero.ctaPrimary")}
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
